import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Columns3, List, Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCatalogs, catalogMap, useUsers } from "@/lib/lookups";
import { OPP_FIELDS, SEMAPHORE } from "@/lib/conecta";
import { FieldsForm } from "@/components/finance/FieldsForm";
import { OppCard } from "@/components/conecta/OppCard";
import { useDebounced } from "@/pages/conecta/RedPage";

const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "border-[#bc9538] c-accent bg-[#bc9538]/10" : "c-line c-text-2"}`}>{children}</button>
);

const Kanban = ({ items, stages, onMove }) => (
  <div data-testid="opportunities-kanban" className="-mx-4 px-4 flex gap-3 overflow-x-auto no-scrollbar snap-x pb-2">
    {Object.entries(stages).map(([k, label]) => {
      const col = items.filter((o) => o.stage === k);
      return (
        <div key={k} data-testid={`kanban-column-${k}`} className="snap-start w-64 shrink-0 space-y-2">
          <p className="text-xs font-semibold c-text-2 flex justify-between px-1"><span>{label}</span><span className="c-muted">{col.length}</span></p>
          {col.map((o) => <OppCard key={o.id} o={o} stages={stages} onMove={onMove} compact />)}
        </div>
      );
    })}
  </div>
);

export default function NegociosPage() {
  const nav = useNavigate();
  const cats = useCatalogs();
  const users = useUsers();
  const [view, setView] = useState("lista");
  const [q, setQ] = useState("");
  const [f, setF] = useState({ stage: "", semaphore: "", responsible_user_id: "", overdue: false });
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const dq = useDebounced(q);
  const stages = catalogMap(cats, "stages");

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries({ q: dq, ...f }).filter(([, v]) => v));
    api.get("/opportunities", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [dq, f]);
  useEffect(() => { load(); }, [load]);

  const move = async (o, stage) => {
    try { await api.patch(`/opportunities/${o.id}`, { stage }); toast.success(`Movido a ${stages[stage]}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = async (payload) => {
    try {
      const { data: o } = await api.post("/opportunities", payload);
      setCreating(false);
      toast.success("Negocio creado");
      nav(`/conecta/negocios/${o.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k] === v ? "" : v }));

  return (
    <div data-testid="negocios-page" className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 c-muted" />
          <input data-testid="opportunities-search-input" className="c-input !pl-9" placeholder="Buscar negocio, contacto, sector…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button data-testid="view-toggle-btn" onClick={() => setView(view === "lista" ? "kanban" : "lista")} aria-label="Cambiar vista" className="w-12 rounded-xl c-card flex items-center justify-center">
          {view === "lista" ? <Columns3 className="w-4 h-4" /> : <List className="w-4 h-4" />}
        </button>
      </div>
      <div className="-mx-4 px-4 flex gap-2 overflow-x-auto no-scrollbar">
        {Object.keys(SEMAPHORE).map((s) => <Chip key={s} testid={`filter-semaphore-${s}`} active={f.semaphore === s} onClick={() => toggle("semaphore", s)}>{s[0].toUpperCase() + s.slice(1)}</Chip>)}
        <Chip testid="filter-overdue" active={f.overdue} onClick={() => setF((s) => ({ ...s, overdue: !s.overdue }))}>Paso vencido</Chip>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="filter-stage" className="c-input !py-2 text-sm" value={f.stage} onChange={(e) => setF((s) => ({ ...s, stage: e.target.value }))}>
          <option value="">Etapa: todas</option>
          {Object.entries(stages).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="filter-responsible" className="c-input !py-2 text-sm" value={f.responsible_user_id} onChange={(e) => setF((s) => ({ ...s, responsible_user_id: e.target.value }))}>
          <option value="">Responsable: todos</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div className="flex items-center justify-between">
        <p data-testid="opportunities-count" className="text-sm c-text-2">{data ? `${data.count} negocio(s)` : "…"}</p>
        <button data-testid="create-opportunity-btn" onClick={() => setCreating(true)} className="c-btn-primary px-3.5 py-2 text-sm flex items-center gap-1.5"><Plus className="w-4 h-4" /> Nuevo negocio</button>
      </div>
      {!data ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div> : view === "kanban" ? (
        <Kanban items={data.items} stages={stages} onMove={move} />
      ) : (
        <div data-testid="opportunities-list" className="space-y-2.5">
          {data.items.length === 0 && <p className="c-card p-6 text-sm c-text-2 text-center">Sin negocios con estos filtros.</p>}
          {data.items.map((o) => <OppCard key={o.id} o={o} stages={stages} />)}
        </div>
      )}
      <FieldsForm open={creating} onOpenChange={setCreating} title="Nuevo negocio" fields={OPP_FIELDS} item={null} onSubmit={create} testid="create-opportunity-sheet" />
    </div>
  );
}
